import { Injectable, signal, computed } from '@angular/core';
import { FirmaService } from '../../firma-yonetim/services/firma.service';
import { FikoYTIstekKok } from '@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service';
import type { IFirmaPersonel, IVardiya } from '../../firma-yonetim/interfaces';
import type {
  IPdksAyarlar, IMesaiCarpani, IGunlukKayit, IMolaKayit,
  IAylikOzet, IAylikGunOzet, PuantajGunDurum,
} from '../../firma-yonetim/interfaces/pdks.interface';
import { VARSAYILAN_PDKS_AYARLAR } from '../../firma-yonetim/interfaces/pdks.interface';

// ─── Yardımcılar ─────────────────────────────────────────────────────────────

function dakikaFark(a: string, b: string): number {
  return Math.max(0, Math.round((new Date(b).getTime() - new Date(a).getTime()) / 60000));
}
function sureStr(ms: number): string {
  if (ms <= 0) return '00:00:00';
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 3600).toString().padStart(2, '0')}:${Math.floor((s % 3600) / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;
}
function gunKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

type GirisCikisTur = 'giris' | 'cikis' | 'mola_baslangic' | 'mola_bitis';
interface IHareket { tur: GirisCikisTur; zaman: string; }

// ─── PDKS Durum Servisi ─────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class PdksDurumService {

  // ─── Signals ────────────────────────────────────────────────────────────────

  aktifPersonel = signal<IFirmaPersonel | null>(null);
  aktifDurum = signal<'bosta' | 'calisiyor' | 'molada'>('bosta');
  girisZamani = signal<string | null>(null);
  molaBaslangic = signal<string | null>(null);
  firmaId = signal('');
  cihazId = signal('');

  // Bugünün tüm kayıtları (çoklu giriş/çıkış)
  gunlukKayitlar = signal<IGunlukKayit[]>([]);
  tumHareketler = signal<IHareket[]>([]);

  // Canlı timer değerleri (her saniye güncellenir)
  canliSure = signal('00:00:00');
  canliMolaSure = signal('0dk');

  // PDKS ayarları (firma bazında)
  pdksAyarlar = signal<IPdksAyarlar>({ firmaId: '', ...VARSAYILAN_PDKS_AYARLAR });

  // Aktif vardiya
  aktifVardiya = signal<IVardiya | null>(null);

  // Seçili mesai çarpanı (override, null = otomatik)
  seciliMesaiCarpaniKodu = signal<string | null>(null);

  // ─── Computed ───────────────────────────────────────────────────────────────

  toplamMolaDk = computed(() => {
    return this.gunlukKayitlar().reduce((t, k) => t + k.molaDk, 0);
  });

  toplamCalismaDk = computed(() => {
    return this.gunlukKayitlar().reduce((t, k) => t + k.calismaDk, 0);
  });

  netCalismaDk = computed(() => {
    return Math.max(0, this.toplamCalismaDk());
  });

  aktifMesaiCarpani = computed((): IMesaiCarpani => {
    const ayarlar = this.pdksAyarlar();
    const secili = this.seciliMesaiCarpaniKodu();
    if (secili) {
      const c = ayarlar.mesaiCarpanlari.find(m => m.kod === secili);
      if (c) return c;
    }
    // Otomatik tespit
    return this.otomatikCarpanBul(new Date());
  });

  fazlaMesaiDk = computed(() => {
    const gunluk = this.pdksAyarlar().gunlukCalismaSaati * 60;
    const net = this.netCalismaDk();
    return Math.max(0, net - gunluk);
  });

  normalMesaiDk = computed(() => {
    const gunluk = this.pdksAyarlar().gunlukCalismaSaati * 60;
    return Math.min(this.netCalismaDk(), gunluk);
  });

  private _timerRef: any = null;

  // ─── Timer ──────────────────────────────────────────────────────────────────

  timerBaslat() {
    this.timerDurdur();
    this._timerRef = setInterval(() => this.canliGuncelle(), 1000);
  }

  timerDurdur() {
    if (this._timerRef) { clearInterval(this._timerRef); this._timerRef = null; }
  }

  private canliGuncelle() {
    const kayitlar = this.gunlukKayitlar();
    const hareketler = this.tumHareketler();
    if (!hareketler.length) {
      this.canliSure.set('00:00:00');
      this.canliMolaSure.set('0dk');
      return;
    }

    const now = Date.now();
    let toplamCalismaMs = 0;
    let toplamMolaMs = 0;

    for (const kayit of kayitlar) {
      if (!kayit.giris) continue;
      const girisMs = new Date(kayit.giris).getTime();
      const cikisMs = kayit.cikis ? new Date(kayit.cikis).getTime() : now;
      let molaMs = kayit.molaDk * 60_000;
      // Devam eden mola varsa
      if (kayit.durum === 'molada') {
        const sonMola = kayit.molalar.find(m => !m.bitis);
        if (sonMola) molaMs += (now - new Date(sonMola.baslangic).getTime());
      }
      toplamCalismaMs += Math.max(0, cikisMs - girisMs - molaMs);
      toplamMolaMs += molaMs;
    }

    this.canliSure.set(sureStr(toplamCalismaMs));
    const molaDk = Math.floor(toplamMolaMs / 60_000);
    this.canliMolaSure.set(molaDk > 0 ? `${Math.floor(molaDk / 60) > 0 ? Math.floor(molaDk / 60) + 's ' : ''}${molaDk % 60}dk` : '0dk');
  }

  // ─── Aksiyonlar ─────────────────────────────────────────────────────────────

  async girisYap(firmaService: FirmaService): Promise<boolean> {
    const personel = this.aktifPersonel();
    if (!personel || this.aktifDurum() !== 'bosta') return false;

    // Çift kart okutma kontrolü
    const hareketler = this.tumHareketler();
    const sonGiris = [...hareketler].reverse().find(h => h.tur === 'giris');
    if (sonGiris) {
      const farkSn = (Date.now() - new Date(sonGiris.zaman).getTime()) / 1000;
      if (farkSn < this.pdksAyarlar().kartOkutmaAralikSn) return false;
    }

    await firmaService.girisCikisKaydet(this.firmaId(), {
      personelId: personel._id, kartUid: personel.kartUid || '',
      cihazId: this.cihazId(), tur: 'giris',
    } as any);
    this.aktifDurum.set('calisiyor');
    return true;
  }

  async cikisYap(firmaService: FirmaService): Promise<boolean> {
    const personel = this.aktifPersonel();
    if (!personel || this.aktifDurum() !== 'calisiyor') return false;
    await firmaService.girisCikisKaydet(this.firmaId(), {
      personelId: personel._id, kartUid: personel.kartUid || '',
      cihazId: this.cihazId(), tur: 'cikis',
    } as any);
    this.aktifDurum.set('bosta');
    return true;
  }

  async molaBaslat_(firmaService: FirmaService): Promise<boolean> {
    const personel = this.aktifPersonel();
    if (!personel || this.aktifDurum() !== 'calisiyor') return false;
    await firmaService.molaKaydet(this.firmaId(), personel._id, personel.kartUid || '', this.cihazId(), 'mola_baslangic');
    this.aktifDurum.set('molada');
    return true;
  }

  async molaBitir_(firmaService: FirmaService): Promise<boolean> {
    const personel = this.aktifPersonel();
    if (!personel || this.aktifDurum() !== 'molada') return false;
    await firmaService.molaKaydet(this.firmaId(), personel._id, personel.kartUid || '', this.cihazId(), 'mola_bitis');
    this.aktifDurum.set('calisiyor');
    return true;
  }

  // ─── Veri Yükleme ─────────────────────────────────────────────────────────

  async gunlukVeriYukle(tarih: Date): Promise<void> {
    const personel = this.aktifPersonel();
    if (!personel) return;

    const fId = this.firmaId();
    const ayarlar = this.pdksAyarlar();

    // Gece vardiyası düzeltmesi: başlangıç saatini geri al
    const gun = new Date(tarih); gun.setHours(0, 0, 0, 0);
    if (tarih.getHours() >= ayarlar.geceVardiyasiGunAtamaSaati) {
      gun.setDate(gun.getDate() + 1);
    }

    // Gece vardiyası için önceki günün akşamından başla
    const sorguBaslangic = new Date(gun);
    sorguBaslangic.setDate(sorguBaslangic.getDate() - 1);
    sorguBaslangic.setHours(ayarlar.geceVardiyasiGunAtamaSaati, 0, 0, 0);
    const sorguBitis = new Date(gun);
    sorguBitis.setHours(ayarlar.geceVardiyasiGunAtamaSaati, 0, 0, 0);

    const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.aggregate' });
    istek.parametreler = { aggregate: [
      { $match: { vt: 'FikoYT.sistem.giris_cikis', firmaId: fId, personelId: personel._id, zaman: { $gte: sorguBaslangic.toISOString(), $lt: sorguBitis.toISOString() } } },
      { $sort: { zaman: 1 } },
    ] };
    await istek.isteSync();
    const hamKayitlar: any[] = istek.cevap?.sonuc || [];

    // Hareketleri çözümle — çoklu giriş/çıkış desteği
    const hareketler: IHareket[] = hamKayitlar.map(k => ({ tur: k.tur, zaman: k.zaman }));
    this.tumHareketler.set(hareketler);

    const kayitlar = this.hareketleriCokluCoz(hareketler, ayarlar);
    this.gunlukKayitlar.set(kayitlar);

    // Genel durum
    const sonKayit = kayitlar[kayitlar.length - 1];
    if (sonKayit) {
      this.aktifDurum.set(sonKayit.durum === 'aktif' ? 'calisiyor' : sonKayit.durum === 'molada' ? 'molada' : 'bosta');
      this.girisZamani.set(kayitlar[0]?.giris || null);
      // Son aktif molayı bul
      const aktifMola = sonKayit.molalar.find(m => !m.bitis);
      this.molaBaslangic.set(aktifMola?.baslangic || null);
    } else {
      this.aktifDurum.set('bosta');
      this.girisZamani.set(null);
      this.molaBaslangic.set(null);
    }
  }

  /**
   * Çoklu giriş/çıkış destekli hareket çözümleme.
   * Her giriş-çıkış çifti ayrı bir IGunlukKayit oluşturur.
   */
  hareketleriCokluCoz(hareketler: IHareket[], ayarlar: IPdksAyarlar): IGunlukKayit[] {
    if (!hareketler.length) return [];

    const sorted = [...hareketler].sort((a, b) => a.zaman.localeCompare(b.zaman));

    if (!ayarlar.cokluGirisCikis) {
      // Tek giriş/çıkış modu (eski davranış)
      return [this.tekKayitCoz(sorted)];
    }

    // Çoklu giriş/çıkış modu
    const kayitlar: IGunlukKayit[] = [];
    let aktifKayit: { giris: string; molalar: IMolaKayit[]; molaBaslangic?: string } | null = null;

    for (const h of sorted) {
      if (h.tur === 'giris') {
        // Önceki kayıt varsa ve çıkış yapılmamışsa, otomatik kapat
        if (aktifKayit) {
          kayitlar.push(this.kayitOlustur(aktifKayit.giris, undefined, aktifKayit.molalar));
        }
        aktifKayit = { giris: h.zaman, molalar: [] };
      } else if (h.tur === 'cikis' && aktifKayit) {
        kayitlar.push(this.kayitOlustur(aktifKayit.giris, h.zaman, aktifKayit.molalar));
        aktifKayit = null;
      } else if (h.tur === 'mola_baslangic' && aktifKayit) {
        aktifKayit.molaBaslangic = h.zaman;
      } else if (h.tur === 'mola_bitis' && aktifKayit?.molaBaslangic) {
        aktifKayit.molalar.push({
          baslangic: aktifKayit.molaBaslangic,
          bitis: h.zaman,
          sureDk: dakikaFark(aktifKayit.molaBaslangic, h.zaman),
        });
        aktifKayit.molaBaslangic = undefined;
      }
    }

    // Son aktif kayıt (çıkış yapılmamış)
    if (aktifKayit) {
      const molalar = [...aktifKayit.molalar];
      if (aktifKayit.molaBaslangic) {
        molalar.push({ baslangic: aktifKayit.molaBaslangic, sureDk: 0 }); // devam eden mola
      }
      const kayit = this.kayitOlustur(aktifKayit.giris, undefined, molalar);
      kayit.durum = aktifKayit.molaBaslangic ? 'molada' : 'aktif';
      kayitlar.push(kayit);
    }

    return kayitlar.length ? kayitlar : [];
  }

  /** Offline kuyruk MongoDB'ye aktarılana kadar hareketi ekranda anında gösterir. */
  yerelHareketEkle(tur: GirisCikisTur, zaman = new Date().toISOString()): void {
    const hareketler = [...this.tumHareketler(), { tur, zaman }]
      .sort((a, b) => a.zaman.localeCompare(b.zaman));
    this.tumHareketler.set(hareketler);
    const kayitlar = this.hareketleriCokluCoz(hareketler, this.pdksAyarlar());
    this.gunlukKayitlar.set(kayitlar);
    const son = kayitlar[kayitlar.length - 1];
    this.aktifDurum.set(son?.durum === 'aktif' ? 'calisiyor' : son?.durum === 'molada' ? 'molada' : 'bosta');
    this.canliGuncelle();
  }

  private tekKayitCoz(sorted: IHareket[]): IGunlukKayit {
    let giris: string | undefined;
    let cikis: string | undefined;
    const molalar: IMolaKayit[] = [];
    let molaBaslangic: string | undefined;

    for (const h of sorted) {
      if (h.tur === 'giris' && !giris) giris = h.zaman;
      if (h.tur === 'cikis') cikis = h.zaman;
      if (h.tur === 'mola_baslangic') molaBaslangic = h.zaman;
      if (h.tur === 'mola_bitis' && molaBaslangic) {
        molalar.push({ baslangic: molaBaslangic, bitis: h.zaman, sureDk: dakikaFark(molaBaslangic, h.zaman) });
        molaBaslangic = undefined;
      }
    }
    if (molaBaslangic) molalar.push({ baslangic: molaBaslangic, sureDk: 0 });

    return this.kayitOlustur(giris, cikis, molalar, !!molaBaslangic);
  }

  private kayitOlustur(giris?: string, cikis?: string, molalar: IMolaKayit[] = [], molaSuruyor = false): IGunlukKayit {
    const molaDk = molalar.reduce((t, m) => t + m.sureDk, 0);
    const brutDk = giris && cikis ? dakikaFark(giris, cikis) : 0;
    const calismaDk = Math.max(0, brutDk - molaDk);
    let durum: IGunlukKayit['durum'] = 'gelmedi';
    if (giris && !cikis) durum = molaSuruyor ? 'molada' : 'aktif';
    else if (giris && cikis) durum = 'cikti';
    return { giris, cikis, molalar, calismaDk, molaDk, durum };
  }

  // ─── Mesai Çarpan Tespiti ─────────────────────────────────────────────────

  otomatikCarpanBul(tarih: Date): IMesaiCarpani {
    const ayarlar = this.pdksAyarlar();
    const gun = tarih.getDay(); // 0=Paz
    const tarihStr = gunKey(tarih);

    // Resmi tatil kontrolü
    if (ayarlar.resmiTatiller.some(t => t.tarih === tarihStr)) {
      const c = ayarlar.mesaiCarpanlari.find(m => m.otomatikUygula && m.uygulamaKosulu === 'resmi_tatil');
      if (c) return c;
    }

    // Pazar
    if (gun === 0) {
      const c = ayarlar.mesaiCarpanlari.find(m => m.otomatikUygula && m.uygulamaKosulu === 'pazar');
      if (c) return c;
    }

    // Cumartesi
    if (gun === 6) {
      const c = ayarlar.mesaiCarpanlari.find(m => m.otomatikUygula && m.uygulamaKosulu === 'cumartesi');
      if (c) return c;
    }

    // Hafta sonu (genel)
    if (gun === 0 || gun === 6) {
      const c = ayarlar.mesaiCarpanlari.find(m => m.otomatikUygula && m.uygulamaKosulu === 'hafta_sonu');
      if (c) return c;
    }

    // Gece vardiyası kontrolü
    const aktifVardiya = this.aktifVardiya();
    if (aktifVardiya) {
      const basSaat = parseInt(aktifVardiya.baslangicSaati?.split(':')[0] || '0');
      if (basSaat >= ayarlar.geceVardiyasiGunAtamaSaati) {
        const c = ayarlar.mesaiCarpanlari.find(m => m.otomatikUygula && m.uygulamaKosulu === 'gece_vardiyasi');
        if (c) return c;
      }
    }

    // Hafta içi (default)
    return ayarlar.mesaiCarpanlari.find(m => m.otomatikUygula && m.uygulamaKosulu === 'hafta_ici')
      || { kod: 'varsayilan', etiket: 'Normal', carpan: 1.0, renk: '#52c41a', otomatikUygula: true };
  }

  // ─── Aylık Özet Hesaplama ─────────────────────────────────────────────────

  async aylikOzetHesapla(yil: number, ay: number): Promise<IAylikOzet> {
    const personel = this.aktifPersonel();
    const fId = this.firmaId();
    if (!personel || !fId) {
      return { gunler: [], toplamNormalDk: 0, toplamFazlaMesaiDk: 0, toplamMolaDk: 0, devamsizGunSayisi: 0, izinliGunSayisi: 0, raporluGunSayisi: 0, calismaGunSayisi: 0, tatilGunSayisi: 0 };
    }

    const ayarlar = this.pdksAyarlar();
    const ayBas = new Date(yil, ay, 1);
    const aySon = new Date(yil, ay + 1, 1);

    // Tüm ay verilerini çek
    const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.aggregate' });
    istek.parametreler = { aggregate: [
      { $match: { vt: 'FikoYT.sistem.giris_cikis', firmaId: fId, personelId: personel._id, zaman: { $gte: ayBas.toISOString(), $lt: aySon.toISOString() } } },
      { $sort: { zaman: 1 } },
      { $group: { _id: { $dateToString: { format: '%Y-%m-%d', date: { $toDate: '$zaman' }, timezone: 'Europe/Istanbul' } }, hareketler: { $push: { tur: '$tur', zaman: '$zaman' } } } },
    ] };
    await istek.isteSync();
    const ham: any[] = istek.cevap?.sonuc || [];

    const gunlukCalismaSaatiDk = ayarlar.gunlukCalismaSaati * 60;
    const calismaGunleri = personel.calismaGunleri || ayarlar.varsayilanCalismaGunleri;

    let toplamNormalDk = 0;
    let toplamFazlaMesaiDk = 0;
    let toplamMolaDk = 0;
    let devamsizGunSayisi = 0;
    let izinliGunSayisi = 0;
    let raporluGunSayisi = 0;
    let calismaGunSayisi = 0;
    let tatilGunSayisi = 0;
    const gunler: IAylikGunOzet[] = [];

    // Her gün için hesapla
    const gunSayisi = new Date(yil, ay + 1, 0).getDate();
    const hamMap = new Map(ham.map(h => [h._id, h.hareketler]));

    for (let g = 1; g <= gunSayisi; g++) {
      const tarih = new Date(yil, ay, g);
      const tarihStr = gunKey(tarih);
      const gunHaftaGunu = tarih.getDay();
      const calismaGunuMu = calismaGunleri.includes(gunHaftaGunu);
      const tatilMi = ayarlar.resmiTatiller.some(t => t.tarih === tarihStr);

      const hareketler: IHareket[] = hamMap.get(tarihStr) || [];
      const kayitlar = this.hareketleriCokluCoz(hareketler, ayarlar);
      const toplamCalDk = kayitlar.reduce((t, k) => t + k.calismaDk, 0);
      const toplamMolDk = kayitlar.reduce((t, k) => t + k.molaDk, 0);
      const fazlaMesai = Math.max(0, toplamCalDk - gunlukCalismaSaatiDk);
      const normalMesai = Math.min(toplamCalDk, gunlukCalismaSaatiDk);

      // Çarpan tespiti
      const carpan = this.otomatikCarpanBul(tarih);

      // Geç geliş hesaplama
      let gecGelisDk = 0;
      if (kayitlar.length > 0 && kayitlar[0].giris && this.aktifVardiya()) {
        const vardiyaBas = this.aktifVardiya()!.baslangicSaati;
        const girisDate = new Date(kayitlar[0].giris);
        const vardiyaDate = new Date(tarihStr + 'T' + vardiyaBas);
        const farkDk = Math.round((girisDate.getTime() - vardiyaDate.getTime()) / 60000);
        if (farkDk > ayarlar.girisToleransDk) gecGelisDk = farkDk;
      }

      // Durum tespiti
      let durum: PuantajGunDurum = 'normal';
      if (tatilMi) { durum = 'tatil'; tatilGunSayisi++; }
      else if (!calismaGunuMu && hareketler.length === 0) { durum = 'calisma_gunu_degil'; }
      else if (hareketler.length === 0 && calismaGunuMu && tarih < new Date()) { durum = 'devamsiz'; devamsizGunSayisi++; }
      else if (hareketler.length > 0) {
        calismaGunSayisi++;
        if (gecGelisDk > 0) durum = 'gec_geldi';
        if (fazlaMesai > 0) durum = 'mesaili';
      }

      toplamNormalDk += normalMesai;
      toplamFazlaMesaiDk += fazlaMesai;
      toplamMolaDk += toplamMolDk;

      gunler.push({
        tarih: tarihStr,
        kayitlar,
        toplamCalismaDk: toplamCalDk,
        toplamMolaDk: toplamMolDk,
        fazlaMesaiDk: fazlaMesai,
        gecGelisDk,
        erkenCikisDk: 0,
        mesaiCarpani: carpan.carpan,
        mesaiCarpanKodu: carpan.kod,
        durum,
        bayraklar: tatilMi ? ['tatil'] : [],
      });
    }

    return { gunler, toplamNormalDk, toplamFazlaMesaiDk, toplamMolaDk, devamsizGunSayisi, izinliGunSayisi, raporluGunSayisi, calismaGunSayisi, tatilGunSayisi };
  }

  // ─── PDKS Ayarları Yükleme ────────────────────────────────────────────────

  async pdksAyarlarYukle(firmaId: string): Promise<void> {
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.aggregate' });
      istek.parametreler = { aggregate: [
        { $match: { vt: 'FikoYT.sistem.pdks_ayarlar' } },
        { $match: { firmaId } },
        { $limit: 1 },
      ] };
      await istek.isteSync();
      const sonuc = istek.cevap?.sonuc?.[0];
      if (sonuc) {
        this.pdksAyarlar.set({ ...VARSAYILAN_PDKS_AYARLAR, ...sonuc });
      } else {
        this.pdksAyarlar.set({ firmaId, ...VARSAYILAN_PDKS_AYARLAR });
      }
    } catch {
      this.pdksAyarlar.set({ firmaId, ...VARSAYILAN_PDKS_AYARLAR });
    }
  }

  // ─── Sıfırlama ────────────────────────────────────────────────────────────

  sifirla() {
    this.timerDurdur();
    this.aktifPersonel.set(null);
    this.aktifDurum.set('bosta');
    this.girisZamani.set(null);
    this.molaBaslangic.set(null);
    this.gunlukKayitlar.set([]);
    this.tumHareketler.set([]);
    this.canliSure.set('00:00:00');
    this.canliMolaSure.set('0dk');
    this.seciliMesaiCarpaniKodu.set(null);
  }
}
